import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useTrackCreatedAnonymousAccount } from "@air/analytics/src/events/useTrackCreatedAnonymousAccount";
import { useTrackLoggedInToAnonymousAccount } from "@air/analytics/src/events/useTrackLoggedInToAnonymousAccount";
import { useTrackViewedAnonymousAccountCreationForm } from "@air/analytics/src/events/useTrackViewedAnonymousAccountCreationForm";
import { useToasts } from "@air/provider-toast";
import { memo, useCallback, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useMount } from "__barrel_optimize__?names=useMount!=!react-use";
import invariant from "tiny-invariant";
import { AnonymousSignUpForm } from "~/components/AnonymousSignUpForm/AnonymousSignUpForm";
import { ReCaptchaTermsInfo } from "~/components/AnonymousSignUpForm/ReCaptchaTermsInfo";
import { useAnonymousSignUp } from "~/components/PublicLinkAnonymousSignUpModal/hooks/useAnonymousSignUp";
import { convertUnknownToError, reportErrorToBugsnag } from "~/utils/ErrorUtils";
import { isCypressRun } from "~/utils/PathUtils";
export const AnonymousSignUpFormWrapper = /*#__PURE__*/ memo((param)=>{
    let { onLoggedIn, objectType, objectId, shortId, trackLocation, ...props } = param;
    const reCaptchaRef = useRef(null);
    const { showToast } = useToasts();
    const { loginWithEmail, signupWithEmailAndName } = useAnonymousSignUp();
    const { trackLoggedInToAnonymousAccount } = useTrackLoggedInToAnonymousAccount();
    const { trackCreatedAnonymousAccount } = useTrackCreatedAnonymousAccount();
    const { trackViewedAnonymousAccountCreationForm } = useTrackViewedAnonymousAccountCreationForm();
    const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    invariant(siteKey, "RECAPTCHA_SITE_KEY is not defined");
    useMount(()=>{
        trackViewedAnonymousAccountCreationForm({
            share_url: shortId,
            object_id: objectId,
            object_type: objectType,
            location: trackLocation
        });
    });
    const getRecaptchaToken = useCallback(async ()=>{
        var // ReCaptcha must be reset before getting a new value,
        // One token can be used only one time to verify a user, so everytime we have to get a new value
        _reCaptchaRef_current;
        if (isCypressRun()) {
            // Return dummy token for cypress, it will be ignored anyway
            return "test-token";
        }
        (_reCaptchaRef_current = reCaptchaRef.current) === null || _reCaptchaRef_current === void 0 ? void 0 : _reCaptchaRef_current.reset();
        try {
            var _reCaptchaRef_current1;
            const token = await ((_reCaptchaRef_current1 = reCaptchaRef.current) === null || _reCaptchaRef_current1 === void 0 ? void 0 : _reCaptchaRef_current1.executeAsync());
            if (!token) {
                throw new Error("Can not get ReCaptcha token");
            }
            return token;
        } catch (error) {
            reportErrorToBugsnag({
                error: convertUnknownToError(error),
                context: "Can not get ReCaptcha token"
            });
            throw error;
        }
    }, []);
    const onSubmitEmail = useCallback(async (email)=>{
        try {
            const recaptchaToken = await getRecaptchaToken();
            if (!recaptchaToken) {
                throw new Error("Can not get ReCaptcha token");
            }
            const account = await loginWithEmail({
                email,
                recaptchaToken
            });
            if (account) {
                trackLoggedInToAnonymousAccount({
                    share_url: shortId,
                    object_id: objectId,
                    object_type: objectType,
                    location: trackLocation
                });
                onLoggedIn();
            }
            return account;
        } catch (error) {
            showToast("Unknown error occurred", {
                type: "assertive",
                withCloseButton: true
            });
            throw error;
        }
    }, [
        getRecaptchaToken,
        loginWithEmail,
        objectId,
        objectType,
        onLoggedIn,
        shortId,
        showToast,
        trackLocation,
        trackLoggedInToAnonymousAccount
    ]);
    const onSignUp = useCallback(async (email, name)=>{
        try {
            const recaptchaToken = await getRecaptchaToken();
            if (!recaptchaToken) {
                throw new Error("Can not get ReCaptcha token");
            }
            await signupWithEmailAndName({
                email,
                name,
                recaptchaToken
            });
            trackCreatedAnonymousAccount({
                share_url: shortId,
                object_id: objectId,
                object_type: objectType,
                location: trackLocation
            });
            onLoggedIn();
        } catch (_error) {
            showToast("Unknown error occurred", {
                type: "assertive",
                withCloseButton: true
            });
        }
    }, [
        getRecaptchaToken,
        objectId,
        objectType,
        onLoggedIn,
        shortId,
        showToast,
        signupWithEmailAndName,
        trackCreatedAnonymousAccount,
        trackLocation
    ]);
    return /*#__PURE__*/ _jsx(AnonymousSignUpForm, {
        ...props,
        onSubmitEmail: onSubmitEmail,
        onSignUp: onSignUp,
        children: /*#__PURE__*/ _jsxs("div", {
            children: [
                !!siteKey && /*#__PURE__*/ _jsx(ReCAPTCHA, {
                    ref: reCaptchaRef,
                    size: "invisible",
                    sitekey: siteKey
                }),
                /*#__PURE__*/ _jsx(ReCaptchaTermsInfo, {})
            ]
        })
    });
});
AnonymousSignUpFormWrapper.displayName = "AnonymousSignUpFormWrapper";
